import { Fragment, useEffect, useState } from 'react'
import {
  api,
  TradesRefused,
  type EvaluationRun,
  type VerdictReportFund,
  type VerdictState,
  type WhatIfBasis,
  type WhatIfResult,
} from '../api'
import { AddOnFootnote, EvaluatorName } from '../components/AddOn'
import { FundPicker, useFunds } from '../components/FundPicker'
import { RowMenu } from '../components/RowMenu'
import { says } from '../limits'
import { amount, asAt, covers, room, rowName, why } from '../verdictWords'

/**
 * What-if trades — proposal §5.3, IMPLEMENTATION.md decision 49. The Snapshot Evaluator pack.
 *
 * Three steps, as tabs: **the fund**, with a summary of the holdings the trades will be
 * applied to and where its latest verdicts stand; **the trades**, added, removed or
 * cleared; and **the results**, which appear once the trades are evaluated. Changing a
 * trade clears the results, so no answer is shown beside trades it did not judge.
 *
 * The counts read as the Excel pane's do, one pill a state: *0 → 1 Breach*. The limits
 * that change come first. Advisory, and said so beside where pre-trade checks run:
 * nothing is traded, and the holdings are left as they were loaded.
 */

type Step = 'fund' | 'trades' | 'results'

const STATES: VerdictState[] = ['breach', 'not_evaluable', 'warn', 'pass']

const LABEL: Record<VerdictState, string> = {
  breach: 'Breach',
  not_evaluable: 'Not evaluable',
  warn: 'Warn',
  pass: 'Pass',
}

interface Draft {
  key: number
  side: 'buy' | 'sell'
  security: string
  amount: string
  attributes: Record<string, string>
}

let drafted = 0
const blank = (): Draft => ({ key: ++drafted, side: 'buy', security: '', amount: '', attributes: {} })

function said(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const parsed = (value: string) => Number(value.replace(/,/g, '').trim())

/** Limits written as words are counted apart, as the Verdicts screen counts them. */
function judged(counts: Record<VerdictState, number>, textOnly: number): Record<VerdictState, number> {
  return { ...counts, not_evaluable: Math.max(0, counts.not_evaluable - textOnly) }
}

function runCounts(run: EvaluationRun): Record<VerdictState, number> {
  return judged(run.counts, run.text_only ?? 0)
}

/** One pill a state: the count, or the count before and after, then the state. */
function Counts({
  after,
  before,
  testId,
}: {
  after: Record<VerdictState, number>
  before?: Record<VerdictState, number>
  testId: string
}) {
  return (
    <div className="whatif-counts" data-testid={testId}>
      {STATES.map((s) => {
        const none = after[s] === 0 && (!before || before[s] === 0)
        return (
          <span key={s} className={`verdict-count ${s}${none ? ' zero' : ''}`} data-testid={`${testId}-${s}`}>
            {before ? `${before[s]} → ${after[s]}` : after[s]} {LABEL[s]}
          </span>
        )
      })}
    </div>
  )
}

export function WhatIf() {
  const { funds } = useFunds()
  const [step, setStep] = useState<Step>('fund')
  const [fund, setFund] = useState('')
  const [basis, setBasis] = useState<WhatIfBasis | null>(null)
  const [standing, setStanding] = useState<VerdictReportFund | null>(null)
  const [trades, setTrades] = useState<Draft[]>([blank()])
  const [result, setResult] = useState<WhatIfResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [problems, setProblems] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [every, setEvery] = useState(false)

  useEffect(() => {
    setBasis(null)
    setStanding(null)
    setResult(null)
    setProblems([])
    setError(null)
    setTrades([blank()])
    setStep('fund')
    if (!fund) return
    let live = true
    api
      .whatIfBasis(fund)
      .then((found) => live && setBasis(found))
      .catch((e) => live && setError(said(e)))
    api
      .verdictReport({ portfolioId: fund })
      .then((report) => live && setStanding(report.funds[0] ?? null))
      .catch(() => live && setStanding(null))
    return () => {
      live = false
    }
  }, [fund])

  const held = new Map((basis?.holdings ?? []).map((h) => [h.security_id, h]))
  const snapshot = basis?.snapshot ?? null
  const ready = trades.some((t) => t.security.trim() && parsed(t.amount) > 0)
  const entered = trades.filter((t) => t.security.trim()).length

  function edited(next: Draft[]) {
    setTrades(next)
    setResult(null)
    setProblems([])
    setError(null)
  }

  function update(key: number, change: Partial<Draft>) {
    edited(trades.map((t) => (t.key === key ? { ...t, ...change } : t)))
  }

  function clear() {
    edited([blank()])
    setStep('trades')
  }

  async function evaluate() {
    if (!snapshot) return
    setBusy(true)
    setError(null)
    setProblems([])
    try {
      const found = await api.whatIf(
        snapshot.id,
        trades
          .filter((t) => t.security.trim())
          .map((t) => ({
            side: t.side,
            security_id: t.security.trim(),
            amount: parsed(t.amount),
            attributes: held.has(t.security.trim())
              ? {}
              : Object.fromEntries(Object.entries(t.attributes).filter(([, v]) => v.trim())),
          })),
      )
      setResult(found)
      setStep('results')
    } catch (e) {
      setResult(null)
      setError(said(e))
      if (e instanceof TradesRefused) setProblems(e.problems)
    } finally {
      setBusy(false)
    }
  }

  const nav = snapshot?.figures.nav
  const share = (value: number) => (nav ? ` · ${((value / nav) * 100).toFixed(2)}% of NAV` : '')
  const cash = basis?.cash_line ? held.get(basis.cash_line) : undefined
  const largest = [...(basis?.holdings ?? [])]
    .filter((h) => h.security_id !== basis?.cash_line)
    .sort((a, b) => b.market_value - a.market_value)
    .slice(0, 5)
  const shown = (result?.verdicts ?? []).filter((v) => v.template !== 'narrative' && (every || v.changed))

  const errorLine = error && (
    <p className="error" role="alert">
      {error}
    </p>
  )

  return (
    <section className="whatif">
      <header className="page-head">
        <h2>What-if trades</h2>
        <p className="note">
          Candidate buys and sells applied to a fund’s newest holdings, and the fund judged before and after them
          against its limits. Advisory: nothing is traded, and the holdings stay as they were loaded. Pre-trade checks
          run in your OMS against the exported rules. Part of the <EvaluatorName strong={false} />.
          <AddOnFootnote />
        </p>
      </header>

      <div className="tabstrip sub" role="tablist" aria-label="What-if steps">
        <button type="button" role="tab" aria-selected={step === 'fund'} onClick={() => setStep('fund')}>
          1. Fund
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={step === 'trades'}
          disabled={!snapshot}
          onClick={() => setStep('trades')}
        >
          2. Trades
          {entered > 0 && <span className="count">{entered}</span>}
        </button>
        {result && (
          <button type="button" role="tab" aria-selected={step === 'results'} onClick={() => setStep('results')}>
            3. Results
          </button>
        )}
      </div>

      {step === 'fund' && (
        <div className="whatif-part" data-testid="whatif-step-fund">
          <FundPicker label="Fund" value={fund} onChange={setFund} funds={funds} testId="whatif-fund" />
          {errorLine}
          {fund && !basis && !error && <p className="loading">Loading…</p>}
          {basis && !snapshot && (
            <p className="empty" data-testid="whatif-no-holdings">
              No holdings loaded for this fund yet. Load a snapshot first.
            </p>
          )}
          {snapshot && basis && (
            <>
              <div className="table-scroll">
                <table className="grid facts-table" data-testid="whatif-summary">
                  <tbody>
                    <tr>
                      <th scope="row">Fund</th>
                      <td>
                        {snapshot.portfolio_code}
                        {snapshot.portfolio_name !== snapshot.portfolio_code && (
                          <span className="clause">{snapshot.portfolio_name}</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Holdings as at</th>
                      <td>
                        {asAt(snapshot.as_of)}
                        <span className="clause">{snapshot.filename}</span>
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Positions</th>
                      <td>{snapshot.position_count}</td>
                    </tr>
                    <tr>
                      <th scope="row">NAV</th>
                      <td>{nav ? `${snapshot.base_currency} ${money(nav)}` : 'Not stated in the snapshot'}</td>
                    </tr>
                    <tr>
                      <th scope="row">Cash</th>
                      <td>
                        {cash ? (
                          <>
                            {money(cash.market_value)}
                            {share(cash.market_value)}
                            <span className="clause">Trades settle in {cash.security_id}</span>
                          </>
                        ) : (
                          'Stays as the snapshot holds it'
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Latest verdicts</th>
                      <td>
                        {standing?.run ? (
                          <>
                            <Counts after={runCounts(standing.run)} testId="whatif-standing" />
                            <span className="clause">
                              On the holdings as at {asAt(standing.run.snapshot_as_of)}
                              {standing.unevaluated_snapshot &&
                                `; holdings as at ${asAt(standing.unevaluated_snapshot.as_of)} not yet evaluated`}
                            </span>
                          </>
                        ) : (
                          'Not evaluated yet'
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Largest holdings</th>
                      <td>
                        {largest.map((h) => (
                          <span key={h.security_id} className="whatif-holding">
                            {h.security_id}
                            {h.issuer_id && ` · ${h.issuer_id}`}: {money(h.market_value)}
                            {share(h.market_value)}
                          </span>
                        ))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="actions">
                <button type="button" onClick={() => setStep('trades')}>
                  Next: trades
                </button>
              </p>
            </>
          )}
        </div>
      )}

      {step === 'trades' && snapshot && basis && (
        <div className="whatif-part" data-testid="whatif-step-trades">
          <p className="note">
            Trades on {snapshot.portfolio_code}’s holdings as at {asAt(snapshot.as_of)}, amounts in{' '}
            {snapshot.base_currency}.
          </p>
          <datalist id="whatif-held">
            {basis.holdings.map((h) => (
              <option key={h.security_id} value={h.security_id}>
                {h.issuer_id ?? ''}
              </option>
            ))}
          </datalist>
          <div className="table-scroll">
            <table className="grid" data-testid="whatif-trades">
              <thead>
                <tr>
                  <th>Side</th>
                  <th>Security</th>
                  <th>Amount ({snapshot.base_currency})</th>
                  <th>Held now</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {trades.map((t, i) => {
                  const id = t.security.trim()
                  const holding = held.get(id)
                  const isNew = Boolean(id) && !holding
                  return (
                    <Fragment key={t.key}>
                      <tr data-testid="whatif-trade">
                        <td>
                          <select
                            aria-label={`Side of trade ${i + 1}`}
                            value={t.side}
                            onChange={(e) => update(t.key, { side: e.target.value as Draft['side'] })}
                          >
                            <option value="buy">Buy</option>
                            <option value="sell">Sell</option>
                          </select>
                        </td>
                        <td>
                          <input
                            aria-label={`Security of trade ${i + 1}`}
                            list="whatif-held"
                            value={t.security}
                            placeholder="ISIN or security identifier"
                            onChange={(e) => update(t.key, { security: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            aria-label={`Amount of trade ${i + 1}`}
                            inputMode="decimal"
                            value={t.amount}
                            onChange={(e) => update(t.key, { amount: e.target.value })}
                          />
                        </td>
                        <td>
                          {holding
                            ? `${money(holding.market_value)}${share(holding.market_value)}`
                            : isNew
                              ? t.side === 'sell'
                                ? 'Not held'
                                : 'New holding'
                              : ''}
                        </td>
                        <td className="run-actions">
                          <RowMenu
                            label={`Actions for trade ${i + 1}`}
                            items={[
                              {
                                label: 'Remove',
                                disabled: trades.length === 1,
                                why: trades.length === 1 ? 'Use Clear to start again.' : undefined,
                                onSelect: () => edited(trades.filter((x) => x.key !== t.key)),
                              },
                            ]}
                          />
                        </td>
                      </tr>
                      {isNew && t.side === 'buy' && basis.fields.length > 0 && (
                        <tr className="whatif-new">
                          <td colSpan={5}>
                            <span className="clause">
                              What is known of {id}. Anything left blank counts as unknown, never as within a limit.
                            </span>
                            <div className="whatif-fields">
                              {basis.fields.map((f) => (
                                <label key={f.column}>
                                  {f.label}
                                  <input
                                    type={f.kind === 'date' ? 'date' : 'text'}
                                    value={t.attributes[f.column] ?? ''}
                                    onChange={(e) =>
                                      update(t.key, { attributes: { ...t.attributes, [f.column]: e.target.value } })
                                    }
                                  />
                                </label>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className="actions">
            <button type="button" className="ghost" onClick={() => edited([...trades, blank()])}>
              Add a trade
            </button>
            <button type="button" className="ghost" onClick={clear}>
              Clear
            </button>
            <button type="button" disabled={busy || !ready} onClick={() => void evaluate()}>
              {busy ? 'Evaluating…' : 'Evaluate'}
            </button>
          </p>
          {errorLine}
          {problems.length > 0 && (
            <ul className="error-list" data-testid="whatif-problems">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {step === 'results' && result && (
        <div className="whatif-part" data-testid="whatif-result">
          <p className="note">
            {result.portfolio_code}’s holdings as at {asAt(result.snapshot_as_of)} with these trades, judged against the
            limits in force that day.{' '}
            {result.changed === 0
              ? 'No limit changes.'
              : `${result.changed} limit${result.changed === 1 ? ' changes' : 's change'}.`}
          </p>
          <Counts
            before={judged(result.counts_before, result.text_only)}
            after={judged(result.counts_after, result.text_only)}
            testId="whatif-totals"
          />

          <h3>Trades</h3>
          <div className="table-scroll">
            <table className="grid" data-testid="whatif-applied">
              <thead>
                <tr>
                  <th>Side</th>
                  <th>Security</th>
                  <th className="num">Amount</th>
                  <th className="num">Held before</th>
                  <th className="num">Held after</th>
                </tr>
              </thead>
              <tbody>
                {result.trades.map((t, i) => (
                  <tr key={i}>
                    <td>{t.side === 'buy' ? 'Buy' : 'Sell'}</td>
                    <td>
                      {t.security_id}
                      {t.new && <span className="clause">A new holding</span>}
                    </td>
                    <td className="num">{money(t.amount)}</td>
                    <td className="num">{money(t.held_before)}</td>
                    <td className="num">{money(t.held_after)}</td>
                  </tr>
                ))}
                {result.cash && (
                  <tr>
                    <td>Cash</td>
                    <td>{result.cash.security_id}</td>
                    <td />
                    <td className="num">{money(result.cash.before)}</td>
                    <td className="num">{money(result.cash.after)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <h3>Limits</h3>
          <label className="checkbox">
            <input type="checkbox" checked={every} onChange={(e) => setEvery(e.target.checked)} />
            Show every limit
          </label>
          <div className="table-scroll">
            <table className="grid stacked" data-testid="whatif-verdicts">
              <thead>
                <tr>
                  <th>Limit</th>
                  <th>Before</th>
                  <th>After</th>
                  <th>Found</th>
                  <th>Why</th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={5} className="empty">
                      No limit changes with these trades.
                    </td>
                  </tr>
                )}
                {shown.map((v) => (
                  <tr key={v.rule_id} data-testid="whatif-verdict">
                    <td>
                      <strong>{rowName(v.rule_key)}</strong>
                      <span className="clause">
                        {says({ operator: v.operator, threshold: v.threshold, measure: v.measure, template: v.template }).text}
                      </span>
                      <span className="clause">{covers(v)}</span>
                    </td>
                    <td>
                      <span className={`verdict-count ${v.before.state}`}>{LABEL[v.before.state]}</span>
                    </td>
                    <td>
                      <span className={`verdict-count ${v.state}`}>{LABEL[v.state]}</span>
                    </td>
                    <td>
                      {[amount(v.before.value, v.unit), amount(v.value, v.unit)].filter(Boolean).join(' → ')}
                      {room(v) && <span className="clause">{room(v)}</span>}
                    </td>
                    <td className="clause">{why(v, result)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="actions">
            <button type="button" className="ghost" onClick={() => setStep('trades')}>
              Change trades
            </button>
            <button type="button" className="ghost" onClick={clear}>
              Clear
            </button>
          </p>
        </div>
      )}
    </section>
  )
}
